import { Button } from '@ipfabric/ui-components/Button/Button';
import { Dialog } from '@ipfabric/ui-components/Dialog/Dialog';
import { SegmentButton } from '@ipfabric/ui-components/SegmentedButton/SegmentButton/SegmentButton';
import { SegmentedButton } from '@ipfabric/ui-components/SegmentedButton/SegmentedButton';
import type { featureFlagNames } from '@ipfabric/utils/featureFlag';
import { type FC, Fragment, useState } from 'react';

import {
  getLocalFeatureFlags,
  removeLocalFeatureFlag,
  toggleLocalFeatureFlag,
} from './featureFlagLocalStorage';

interface FeatureFlagDialogProps {
  onClose: () => void;
}

/** Feature flags that are useful for development and should be shown in the dialog. */
const devFeatureFlags = [
  'ENABLE_HOOK_FORM_DEV_TOOLS',
  'ENABLE_LEAVE_DIALOG_DEBUG_INDICATOR',
  'ENABLE_GRAPH_DEBUG_HIDDEN_NODE_REASONS',
  'ENABLE_GRAPH_DUMPING_RENDERER_REQUEST_SOURCE',
  'ENABLE_DEBUG_PATHLOOKUP',
] as const satisfies readonly (typeof featureFlagNames)[number][];

type DevFeatureFlag = (typeof devFeatureFlags)[number];

type FlagState = 'on' | 'off' | 'default';

const getFlagState = (
  flag: DevFeatureFlag,
  localFlags: Partial<Record<string, boolean>>,
): FlagState => {
  if (flag in localFlags) {
    return localFlags[flag] ? 'on' : 'off';
  }
  return 'default';
};

export const FeatureFlagDialog: FC<FeatureFlagDialogProps> = ({ onClose }) => {
  const [localFlags, setLocalFlags] = useState(getLocalFeatureFlags);

  const handleStateChange = (flag: DevFeatureFlag, newState: FlagState) => {
    if (newState === 'on') {
      toggleLocalFeatureFlag(flag, true);
      setLocalFlags((prev) => ({ ...prev, [flag]: true }));
    } else if (newState === 'off') {
      toggleLocalFeatureFlag(flag, false);
      setLocalFlags((prev) => ({ ...prev, [flag]: false }));
    } else {
      // Default - remove override
      removeLocalFeatureFlag(flag);
      setLocalFlags((prev) => {
        const { [flag]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Dialog
      header={{ title: 'Feature Flags (Local Overrides)' }}
      onClose={onClose}
      closeOnBackdropClick
      size='large'
      footer={({ closeModal }) => (
        <div className='flex gap-sm justify-end'>
          <Button variant='outline' onClick={closeModal}>
            Close
          </Button>
          <Button variant='primary' onClick={handleReload} icon='fa-rotate'>
            Reload Page
          </Button>
        </div>
      )}
    >
      <div className='flex flex-col gap-sm'>
        <p className='text-muted text-sm'>
          Toggle feature flags for local development. Changes require a page
          reload to take effect.
        </p>

        <div className='grid grid-cols-[1fr_auto] gap-x-sm gap-y-xs items-center'>
          {devFeatureFlags.map((flag) => {
            const state = getFlagState(flag, localFlags);
            return (
              <Fragment key={flag}>
                <div className='py-xs px-sm rounded bg-layer-1 hover:bg-layer-2 min-w-0'>
                  <span className='font-medium text-sm break-all'>{flag}</span>
                  <span className='text-xs text-muted block'>
                    {state === 'default' && '(using server default)'}
                    {state === 'on' && '(forced ON)'}
                    {state === 'off' && '(forced OFF)'}
                  </span>
                </div>
                <SegmentedButton.Single
                  value={state}
                  onChange={(newState) =>
                    handleStateChange(flag, newState as FlagState)
                  }
                  size='small'
                >
                  <SegmentButton value='default' icon='fa-minus' />
                  <SegmentButton value='on' icon='fa-check' />
                  <SegmentButton value='off' icon='fa-xmark' />
                </SegmentedButton.Single>
              </Fragment>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
};
